import {
  useCallback,
  useEffect,
  useRef,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';

type TabItem = {
  id: string;
  label: ReactNode;
};

type Props = {
  items: TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  className?: string;
};

const DRAG_THRESHOLD_PX = 8;

/**
 * Horizontal tabs with optional drag-to-scroll.
 * Clicks always change tab unless the user actually dragged.
 */
export function ScrollableTabs({ items, activeId, onChange, className = '' }: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{
    pointerId: number | null;
    startX: number;
    scrollLeft: number;
    moved: boolean;
  }>({
    pointerId: null,
    startX: 0,
    scrollLeft: 0,
    moved: false,
  });
  const suppressClick = useRef(false);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    if (!el) return;
    // Only start potential drag with primary button / touch
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    drag.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      scrollLeft: el.scrollLeft,
      moved: false,
    };
  }, []);

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    const d = drag.current;
    if (!el || d.pointerId !== e.pointerId) return;

    const dx = e.clientX - d.startX;
    if (!d.moved && Math.abs(dx) < DRAG_THRESHOLD_PX) return;

    if (!d.moved) {
      d.moved = true;
      el.classList.add('is-dragging');
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    }

    el.scrollLeft = d.scrollLeft - dx;
  }, []);

  const endDrag = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    const d = drag.current;
    if (d.pointerId !== e.pointerId) return;

    const wasDragging = d.moved;
    d.pointerId = null;
    d.moved = false;

    if (el) {
      el.classList.remove('is-dragging');
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    }

    if (wasDragging) {
      suppressClick.current = true;
    }
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const active = el.querySelector<HTMLElement>('.nav-link.active');
    if (!active) return;
    const left = active.offsetLeft - 24;
    el.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [activeId]);

  return (
    <div className={`scroll-tabs ${className}`.trim()}>
      <div
        ref={scrollerRef}
        className="scroll-tabs__track"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        role="tablist"
      >
        <ul className="nav nav-tabs nav-tabs-pk flex-nowrap mb-0">
          {items.map((item) => (
            <li className="nav-item flex-shrink-0" key={item.id} role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={activeId === item.id}
                className={`nav-link ${activeId === item.id ? 'active' : ''}`}
                onClick={() => {
                  if (suppressClick.current) {
                    suppressClick.current = false;
                    return;
                  }
                  onChange(item.id);
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
